import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Star, ChevronDown } from "lucide-react";
import { NAV, PHONE, PHONE_HREF, LOGO } from "../lib/site";
import { PASSPORT_PRODUCT_LIST } from "../data/passportProducts";
import { SkipLink } from "./a11y/SkipLink";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [passportOpen, setPassportOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const passportRef = useRef(null);
  const passportBtnRef = useRef(null);
  const mobileBtnRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the passport menu on outside click.
  useEffect(() => {
    const onDoc = (e) => { if (passportRef.current && !passportRef.current.contains(e.target)) setPassportOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Escape closes the passport menu / mobile menu and returns focus to the trigger.
  useEffect(() => {
    if (!passportOpen && !open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (passportOpen) { setPassportOpen(false); passportBtnRef.current?.focus(); }
      if (open) { setOpen(false); mobileBtnRef.current?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [passportOpen, open]);

  const isActive = (to) => location.pathname === to;

  const openMenu = () => { clearTimeout(closeTimer.current); setPassportOpen(true); };
  const scheduleClose = () => { closeTimer.current = setTimeout(() => setPassportOpen(false), 150); };

  return (
    <>
      <SkipLink />
      <div role="region" aria-label="Location and rating" data-a11y-dark className="hidden md:flex items-center justify-between bg-[#0b192c] text-white/80 text-xs px-8 py-2 relative z-50">
        <span className="tracking-wide" data-testid="topbar-location">1629 K St NW #300, Washington, DC 20006</span>
        <span className="flex items-center gap-2">
          <Star className="w-3.5 h-3.5 fill-[#f5b301] text-[#f5b301]" aria-hidden="true" />
          4.9 rated · 20+ years of experience · Serving the East Coast &amp; nationwide
        </span>
      </div>

      <header
        className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled ? "backdrop-blur-xl bg-white/85 border-b border-black/5 shadow-[0_4px_30px_rgba(11,25,44,0.06)]" : "bg-white border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between h-[72px]">
          <Link
            to="/"
            className="flex items-center"
            data-testid="logo-link"
            aria-label="Urgent Passport & Visa — home"
            aria-current={isActive("/") ? "page" : undefined}
            onClick={(e) => {
              setOpen(false);
              if (location.pathname === "/") {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <img src={LOGO} alt="Urgent Passport & Visa logo" className="h-9 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {NAV.map((item) =>
              item.label === "Passport" ? (
                <div
                  key={item.label}
                  className="relative"
                  ref={passportRef}
                  onMouseEnter={openMenu}
                  onMouseLeave={scheduleClose}
                  data-testid="nav-passport-dropdown"
                >
                  <button
                    type="button"
                    ref={passportBtnRef}
                    onClick={() => setPassportOpen((v) => !v)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-[#0b192c]/80 hover:text-[#d90429] transition-colors"
                    aria-haspopup="true"
                    aria-expanded={passportOpen}
                    aria-controls="passport-menu"
                    data-testid="nav-passport"
                  >
                    Passport <ChevronDown className={`w-4 h-4 transition-transform ${passportOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                  <div
                    id="passport-menu"
                    role="menu"
                    aria-label="Passport services"
                    className={`absolute left-0 top-full pt-3 transition-[opacity,transform,visibility] duration-200 z-50 ${
                      passportOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible translate-y-1"
                    }`}
                  >
                    <div className="w-64 rounded-2xl border border-black/5 bg-white shadow-[0_20px_60px_rgba(11,25,44,0.16)] p-2">
                      {PASSPORT_PRODUCT_LIST.map((prod) => (
                        <Link
                          key={prod.slug}
                          to={`/product/${prod.slug}`}
                          role="menuitem"
                          onClick={() => { setPassportOpen(false); setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          className="block rounded-xl px-3.5 py-2.5 text-sm font-medium text-[#0b192c] hover:bg-[#f8f9fa] hover:text-[#d90429] transition-colors"
                          data-testid={`nav-passport-item-${prod.slug}`}
                          aria-current={isActive(`/product/${prod.slug}`) ? "page" : undefined}
                        >
                          {prod.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="text-sm font-medium text-[#0b192c]/80 hover:text-[#d90429] transition-colors relative group aria-[current=page]:text-[#d90429]"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  aria-current={isActive(item.to) ? "page" : undefined}
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-[#d90429] group-hover:w-full transition-[width] duration-300" aria-hidden="true" />
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={PHONE_HREF}
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#ef233c] transition-colors"
              data-testid="header-call-btn"
              aria-label={`Call Urgent Passport & Visa at ${PHONE}`}
            >
              <Phone className="w-4 h-4" aria-hidden="true" /> {PHONE}
            </a>
            <button
              type="button"
              ref={mobileBtnRef}
              className="lg:hidden p-2 text-[#0b192c]"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="mobile-menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              id="mobile-menu"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden bg-white border-t border-black/5"
            >
              <nav className="px-6 py-4 flex flex-col gap-1" aria-label="Mobile">
                {NAV.map((item) =>
                  item.label === "Passport" ? (
                    <div key={item.label} className="border-b border-black/5 py-2" data-testid="mobile-nav-passport-group">
                      <h2 className="py-1 text-xs font-bold uppercase tracking-wider text-[#d90429]">Passport Services</h2>
                      {PASSPORT_PRODUCT_LIST.map((prod) => (
                        <Link
                          key={prod.slug}
                          to={`/product/${prod.slug}`}
                          onClick={() => { setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          className="block py-2.5 text-base font-medium text-[#0b192c]"
                          data-testid={`mobile-nav-passport-item-${prod.slug}`}
                          aria-current={isActive(`/product/${prod.slug}`) ? "page" : undefined}
                        >
                          {prod.label}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => { setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                      className="text-left py-3 text-base font-medium text-[#0b192c] border-b border-black/5"
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      aria-current={isActive(item.to) ? "page" : undefined}
                    >
                      {item.label}
                    </Link>
                  )
                )}
                <a href={PHONE_HREF} className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#d90429] text-white px-5 py-3 font-semibold" data-testid="mobile-call-btn" aria-label={`Call ${PHONE}`}>
                  <Phone className="w-4 h-4" aria-hidden="true" /> Call {PHONE}
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};
